import XMarkIcon from '@heroicons/react/20/solid/XMarkIcon.js';
import React from 'react';
import { Button } from 'react-aria-components';

import type { LabelColor } from '../../tokens/colors';
import { tv } from '../../tv';

/**
 * A label a user attached: a category, a grouping of their own invention.
 *
 * One of three look-alike components with different owners (see
 * docs/specs/frontend/design-system/badge-status-tag.md): a Badge is what the
 * system counts, a Status is the state it computed, a Tag is what a user
 * attached. Square corners against Status's full round is what tells an
 * attached label from a computed state when the two share a row.
 *
 * `color` is decorative, never semantic: a user's "Q3 audit" tag is not a
 * warning, its hue exists only to make it recognisable across screens. The
 * set is the closed twelve `LabelColor` names, no arbitrary hex, which is
 * what keeps the data portable and dark mode tractable. Absent means the
 * muted gray look; a call site rendering a label without a stored override
 * passes `autoColor(text)` so every screen computes the same hue.
 *
 * Removal is a prop rather than a call-site composition, per the
 * component-map rule: a close glyph hand-wired next to a pill gets invented
 * differently at every site. `removeLabel` is required with `onRemove`
 * because the button is icon-only and the name is all a screen reader gets.
 *
 * Activation (`onClick`/`href` per the spec's geometry table) is DEFERRED:
 * an actionable Tag needs the interaction.md element inference (RAC
 * Link/Button root, the Card pattern), and that root cannot also contain the
 * remove button without nesting interactive elements. What a hovered
 * label-colored chip looks like is also undesigned; interactive Tag lands as
 * its own pass once it is.
 */

/* Three fill styles on every label color, all in the compound table below so
 * each pairing is a literal the theme build can see:
 *
 *   soft     the borderless soft anatomy: step 3 background on the ALPHA
 *            scale so the tag composes onto any surface, step 11 text. The
 *            border is TRANSPARENT rather than absent so all three variants
 *            share exact geometry; borderless is also what the existing
 *            Chakra subtle chips look like, so soft drops into their places
 *            without a visual jump (a visible border may be adopted later)
 *   solid    the scale's solid step 9 fill under white text, the pairing
 *            Radix designs that step for; transparent border, same geometry
 *   outline  the surface's own background, the alpha step 7 border (the
 *            strongest resting border step), step 11 text; the one variant
 *            that draws its border
 *
 * SOLID TEXT SPECIAL CASE. Radix designs the bright solids (sky, mint, lime,
 * yellow, amber) for dark foreground text rather than white, and of our
 * twelve label colors only yellow is in that set: white on yellow 9
 * (#ffe629) measures 1.3:1 while every other scale's step 9 clears 3:1.
 * Yellow therefore takes its step 12 text in light mode (8.7:1). Dark mode
 * keeps the same bright solid but flips step 12 into a LIGHT text colour, so
 * yellow's dark-mode text is the dark scale's step 1, its darkest value
 * (14.8:1). No other scale needs per-mode work: the Radix dark scales keep
 * the white pairing readable by construction. */
export const tagStyles = tv({
  slots: {
    root: [
      'ck:inline-flex ck:items-center ck:gap-1 ck:rounded-4 ck:border',
      'ck:text-xs ck:font-medium ck:min-w-0 ck:max-w-full',
    ],
    icon: 'ck:size-3 ck:shrink-0',
    /* min-w-0 + truncate: a long label inside a tight cell cuts with an
     * ellipsis instead of pushing the tag wider than its column. */
    label: 'ck:min-w-0 ck:truncate',
    /* The remove button inherits the tag's text colour and signals with
     * opacity, so it needs no per-color variant and follows the palette for
     * free. The focus ring is the standard one, snapped rather than
     * transitioned. Its target is the system's 24px square, invisible around
     * the 12px glyph (WCAG 2.5.8): within the tag and the 4px between tags,
     * so it never reaches a neighbor's label. */
    remove: [
      'ck:relative ck:inline-flex ck:items-center ck:justify-center ck:shrink-0 ck:rounded-1',
      'ck:cursor-pointer ck:opacity-70 ck:hover:opacity-100 ck:focus-visible:focus-ring',
      'ck:after:absolute ck:after:top-1/2 ck:after:left-1/2 ck:after:size-6',
      'ck:after:-translate-x-1/2 ck:after:-translate-y-1/2',
    ],
    removeIcon: 'ck:size-3',
  },
  variants: {
    /* The fill style. Empty on purpose: every class is a color x variant
     * pairing, and they all live in the compound table so none is assembled
     * from fragments the theme build cannot see. */
    variant: { soft: {}, solid: {}, outline: {} },
    /* Filled from the compound table's last entry, so it wins over the color it replaces. */
    selection: { true: {}, false: {} },
    /* Gray is the intentional mute, the color a user picks on purpose to
     * de-emphasise a label; autoColor never assigns it. */
    color: {
      red: {},
      orange: {},
      yellow: {},
      green: {},
      teal: {},
      cyan: {},
      blue: {},
      indigo: {},
      purple: {},
      pink: {},
      brown: {},
      gray: {},
    },
    /* sm and md share Status's heights, so the two sit level in the same row:
     * md is 20px (the Figma symbol height and the product's table density),
     * sm the 16px compact step with tighter padding. lg is the 24px label
     * step for taller rows. */
    size: {
      sm: { root: 'ck:h-4 ck:px-pill' },
      md: { root: 'ck:h-5 ck:px-2' },
      lg: { root: 'ck:h-6 ck:px-2 ck:text-sm' },
    },
  },
  compoundVariants: [
    {
      color: 'red',
      variant: 'soft',
      class: { root: 'ck:bg-red-a-3 ck:border-transparent ck:text-red-11' },
    },
    {
      color: 'red',
      variant: 'solid',
      class: { root: 'ck:bg-red-9 ck:border-transparent ck:text-white' },
    },
    {
      color: 'red',
      variant: 'outline',
      class: { root: 'ck:bg-transparent ck:border-red-a-7 ck:text-red-11' },
    },
    {
      color: 'orange',
      variant: 'soft',
      class: { root: 'ck:bg-orange-a-3 ck:border-transparent ck:text-orange-11' },
    },
    {
      color: 'orange',
      variant: 'solid',
      class: { root: 'ck:bg-orange-9 ck:border-transparent ck:text-white' },
    },
    {
      color: 'orange',
      variant: 'outline',
      class: { root: 'ck:bg-transparent ck:border-orange-a-7 ck:text-orange-11' },
    },
    {
      color: 'yellow',
      variant: 'soft',
      class: { root: 'ck:bg-yellow-a-3 ck:border-transparent ck:text-yellow-11' },
    },
    /* The one dark-text solid; see the special case above. */
    {
      color: 'yellow',
      variant: 'solid',
      class: {
        root: 'ck:bg-yellow-9 ck:border-transparent ck:text-yellow-12 ck:dark:text-yellow-1',
      },
    },
    {
      color: 'yellow',
      variant: 'outline',
      class: { root: 'ck:bg-transparent ck:border-yellow-a-7 ck:text-yellow-11' },
    },
    {
      color: 'green',
      variant: 'soft',
      class: { root: 'ck:bg-green-a-3 ck:border-transparent ck:text-green-11' },
    },
    {
      color: 'green',
      variant: 'solid',
      class: { root: 'ck:bg-green-9 ck:border-transparent ck:text-white' },
    },
    {
      color: 'green',
      variant: 'outline',
      class: { root: 'ck:bg-transparent ck:border-green-a-7 ck:text-green-11' },
    },
    {
      color: 'teal',
      variant: 'soft',
      class: { root: 'ck:bg-teal-a-3 ck:border-transparent ck:text-teal-11' },
    },
    {
      color: 'teal',
      variant: 'solid',
      class: { root: 'ck:bg-teal-9 ck:border-transparent ck:text-white' },
    },
    {
      color: 'teal',
      variant: 'outline',
      class: { root: 'ck:bg-transparent ck:border-teal-a-7 ck:text-teal-11' },
    },
    {
      color: 'cyan',
      variant: 'soft',
      class: { root: 'ck:bg-cyan-a-3 ck:border-transparent ck:text-cyan-11' },
    },
    {
      color: 'cyan',
      variant: 'solid',
      class: { root: 'ck:bg-cyan-9 ck:border-transparent ck:text-white' },
    },
    {
      color: 'cyan',
      variant: 'outline',
      class: { root: 'ck:bg-transparent ck:border-cyan-a-7 ck:text-cyan-11' },
    },
    {
      color: 'blue',
      variant: 'soft',
      class: { root: 'ck:bg-blue-a-3 ck:border-transparent ck:text-blue-11' },
    },
    {
      color: 'blue',
      variant: 'solid',
      class: { root: 'ck:bg-blue-9 ck:border-transparent ck:text-white' },
    },
    {
      color: 'blue',
      variant: 'outline',
      class: { root: 'ck:bg-transparent ck:border-blue-a-7 ck:text-blue-11' },
    },
    {
      color: 'indigo',
      variant: 'soft',
      class: { root: 'ck:bg-indigo-a-3 ck:border-transparent ck:text-indigo-11' },
    },
    {
      color: 'indigo',
      variant: 'solid',
      class: { root: 'ck:bg-indigo-9 ck:border-transparent ck:text-white' },
    },
    {
      color: 'indigo',
      variant: 'outline',
      class: { root: 'ck:bg-transparent ck:border-indigo-a-7 ck:text-indigo-11' },
    },
    {
      color: 'purple',
      variant: 'soft',
      class: { root: 'ck:bg-purple-a-3 ck:border-transparent ck:text-purple-11' },
    },
    {
      color: 'purple',
      variant: 'solid',
      class: { root: 'ck:bg-purple-9 ck:border-transparent ck:text-white' },
    },
    {
      color: 'purple',
      variant: 'outline',
      class: { root: 'ck:bg-transparent ck:border-purple-a-7 ck:text-purple-11' },
    },
    {
      color: 'pink',
      variant: 'soft',
      class: { root: 'ck:bg-pink-a-3 ck:border-transparent ck:text-pink-11' },
    },
    {
      color: 'pink',
      variant: 'solid',
      class: { root: 'ck:bg-pink-9 ck:border-transparent ck:text-white' },
    },
    {
      color: 'pink',
      variant: 'outline',
      class: { root: 'ck:bg-transparent ck:border-pink-a-7 ck:text-pink-11' },
    },
    {
      color: 'brown',
      variant: 'soft',
      class: { root: 'ck:bg-brown-a-3 ck:border-transparent ck:text-brown-11' },
    },
    {
      color: 'brown',
      variant: 'solid',
      class: { root: 'ck:bg-brown-9 ck:border-transparent ck:text-white' },
    },
    {
      color: 'brown',
      variant: 'outline',
      class: { root: 'ck:bg-transparent ck:border-brown-a-7 ck:text-brown-11' },
    },
    {
      color: 'gray',
      variant: 'soft',
      class: { root: 'ck:bg-gray-a-3 ck:border-transparent ck:text-gray-11' },
    },
    {
      color: 'gray',
      variant: 'solid',
      class: { root: 'ck:bg-gray-9 ck:border-transparent ck:text-white' },
    },
    {
      color: 'gray',
      variant: 'outline',
      class: { root: 'ck:bg-transparent ck:border-gray-a-7 ck:text-gray-11' },
    },
    { selection: true, class: { root: 'ck:bg-accent-a-3 ck:text-accent-11' } },
  ],
  /* No stored color and no computed one falls back to the mute. */
  defaultVariants: { color: 'gray', variant: 'soft', size: 'md' },
});

interface TagBaseProps {
  /** The label text. */
  children: React.ReactNode;
  /**
   * One of the twelve label colors. Leave it off for the muted gray look;
   * pass `autoColor(text)` when rendering a label with no stored override.
   */
  color?: LabelColor;
  /**
   * The fill style. `soft` (the default) is the standard label look;
   * `solid` fills with the scale's solid step for the rare chip that must
   * carry weight; `outline` keeps the surface's own background for dense
   * compositions. The Status dot is not a variant: a fill style and a state
   * indicator are orthogonal axes, and the dot stays Status-only.
   */
  variant?: 'soft' | 'solid' | 'outline';
  /** sm and md are shared with Status; lg suits taller label rows. */
  size?: 'sm' | 'md' | 'lg';
  /**
   * A chosen item rather than a user's label: the selection's tint, the
   * accent's soft pair, in place of a label color, so the chip matches the
   * chosen option it stands for (a picker's chips, a search's chosen records).
   */
  isSelection?: boolean;
  /**
   * A leading glyph, as a component per the glyph-prop pattern (icon.md):
   * the tag sizes and colours it from its own recipe, so it cannot drift.
   */
  icon?: React.ComponentType<React.SVGProps<SVGSVGElement>>;
  id?: string;
  /** Test hook; end-to-end selectors address a tag by name. */
  'data-testid'?: string;
}

export type TagProps = TagBaseProps &
  (
    | {
        /** Removes the tag. Renders the trailing remove button. */
        onRemove: () => void;
        /**
         * Names the icon-only remove button for assistive technology
         * ("Remove tag Legal"). Required with `onRemove`: the name is all a
         * screen reader gets.
         */
        removeLabel: string;
      }
    | { onRemove?: undefined; removeLabel?: undefined }
  );

export const Tag = React.forwardRef<HTMLSpanElement, TagProps>(function Tag(
  {
    children,
    color,
    variant = 'soft',
    size = 'md',
    isSelection = false,
    icon: IconGlyph,
    onRemove,
    removeLabel,
    ...props
  },
  ref,
) {
  const { root, icon, label, remove, removeIcon } = tagStyles({
    color,
    variant,
    size,
    selection: isSelection,
  });
  return (
    <span {...props} ref={ref} className={root()} data-component="tag">
      {IconGlyph ? <IconGlyph className={icon()} aria-hidden="true" /> : null}
      {/* An empty label would still take the flex gap and pad an icon-only tag. */}
      {children == null ? null : <span className={label()}>{children}</span>}
      {onRemove ? (
        /* Keep removal out of parent select/drag interactions, and keep the
         * combobox input focused so this button stays mounted through press. */
        <Button
          onPress={onRemove}
          onPointerDown={(event) => event.stopPropagation()}
          aria-label={removeLabel}
          className={remove()}
          preventFocusOnPress
        >
          <XMarkIcon className={removeIcon()} aria-hidden />
        </Button>
      ) : null}
    </span>
  );
});
